"use client"
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import Logo from '@/components/Logo'
import type { Category } from '@/lib/api-client'
import type { Locale } from '@/lib/i18n'
import { useNormalizedCategories } from '@/lib/category-normalizer'



type TopNavProps = {
  locale: Locale
  categories: Category[]
}

export default function TopNav({ locale, categories: initialCategories }: TopNavProps) {
  const t = useTranslations('nav')
  const pathname = usePathname()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [categories] = useState<Category[]>(initialCategories)
  const normalizedCategories = useNormalizedCategories(categories)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <header className="sticky top-0 z-50 mb-2 w-full border-b border-gray-800 bg-gray-900/95 backdrop-blur shadow-sm">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Logo variant="header" className="" />
        
        {/* Desktop Navigation */}
        <nav aria-label="Primary" className="ml-auto hidden md:block">
          <ul className="flex items-center gap-6">
            {normalizedCategories.map((category) => {
              const href = `/${locale}/${category.slug}`
              const active = pathname === href
              const categoryName = locale === 'fr' ? category.name_fr : category.name_es
              return (
                <li key={category.slug}>
                  <Link
                    prefetch
                    href={href}
                    className={
                      'text-sm font-medium hover:text-accent focus-visible:text-accent ' +
                      (active ? 'text-accent' : 'text-gray-100')
                    }
                  >
                    {categoryName}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>
        
        {/* Language Switcher */}
        <div className="ml-auto md:ml-4">
          <LanguageSwitcher />
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="ml-2 flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 md:hidden"
          aria-label="Toggle mobile menu"
        >
          <svg
            className={`h-6 w-6 transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-gray-800 bg-gray-900 md:hidden">
          <nav className="mx-auto max-w-7xl px-4 py-4">
            <ul className="space-y-4">
              {normalizedCategories.map((category) => {
                const href = `/${locale}/${category.slug}`
                const active = pathname === href
                const categoryName = locale === 'fr' ? category.name_fr : category.name_es
                return (
                  <li key={category.slug}>
                    <Link
                      prefetch
                      href={href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={
                        'block rounded-md px-3 py-2 text-base font-medium transition-colors hover:bg-gray-800 ' +
                        (active ? 'bg-blue-600/20 text-blue-300' : 'text-gray-200')
                      }
                    >
                      {categoryName}
                    </Link>
                  </li>
                )
              })}
              
              {/* Mobile Links */}
              <li className="border-t border-gray-800 pt-4">
                <Link
                  prefetch
                  href={`/${locale}/videos`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-200 transition-colors hover:bg-gray-800"
                >
                  {t('videos')}
                </Link>
              </li>
              <li>
                <Link
                  prefetch
                  href={`/${locale}/about`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-200 transition-colors hover:bg-gray-800"
                >
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link
                  prefetch
                  href={`/${locale}/contact`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-200 transition-colors hover:bg-gray-800"
                >
                  {t('contact')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  )
}

